import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { CustomKeyboardComponent } from '@stockcare-components/custom-keyboard/custom-keyboard.component';
import { KeyboardTemplate } from '@stockcare-components/custom-keyboard/keyboard-templates';
import { Roles } from '@stockcare-helpers/roles';
import { ActivityService } from '@stockcare-services/activity-service/activity.service';
import { AuthenticationService } from '@stockcare-services/authentication/authentication.service';
import { CryptoService } from '@stockcare-services/crypto/crypto.service';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { NotificationService } from '@stockcare-services/notification-service/notification.service';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { ThemeService } from '@stockcare-services/theme/theme.service';
import { UserStorageService } from '@stockcare-storage/user/user-storage.service';

@Component({
  standalone: false,
  selector: 'app-pin',
  templateUrl: './pin.component.html',
  styleUrls: ['./pin.component.scss'],
})
export class PinComponent {
  @ViewChild('pinKeyboard') pinKeyboard: CustomKeyboardComponent;
  @ViewChild('pinInput') pinInput: ElementRef;
  @Input() userName: string;

  @Output() showLogIn: EventEmitter<any> = new EventEmitter();

  public logo: string;
  public pinTemplate = KeyboardTemplate.Pin;
  placeholderText: string = 'Enter new pin';
  pin: string = '';
  pinEntry: string;
  pinLength: number;
  isDeviceDeactivated: boolean;
  loginError: boolean;
  alert: string;

  constructor(
    public deviceService: DeviceService,
    private authService: AuthenticationService,
    private themeService: ThemeService,
    private userStorageService: UserStorageService,
    private cryptoService: CryptoService,
    private notify: NotificationService,
    private settingsService: SettingsService,
    private activityService: ActivityService,
    private router: Router
  ) {
    if (themeService.getTheme() != null) {
      this.logo = '../../../assets/img/' + themeService.getTheme().logo;
    } else {
      this.logo = '../../../assets/img/stockcare-default-logo.png';
    }
    this.pinLength = this.settingsService.getSettingNumber('userPinLength');
  }

  ngOnInit() {
    this.setPinEntryText();
  }

  public typedText(pin: string): void {
    this.pin = pin;
    this.setPinEntryText();
    this.saveUserPin(pin);
  }

  private setPinEntryText() {
    this.pinEntry = '';
    for (let i = 0; i < this.pinLength; i++) {
      if (i < this.pin.length)
        this.pinEntry += ' \u{2022}';
      else
        this.pinEntry += ' -';
    }
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (/^[0-9]{1}$/.test(event.key))
      this.pin += event.key;
    else if (event.key === 'Backspace')
      this.pin = this.pin.slice(0, -1);
    this.pinKeyboard.keyboard.setInput(this.pin);
    this.setPinEntryText();
    this.saveUserPin(this.pin);
    this.pinKeyboard.onChange(this.pin);
    }

  onClosed() {
    this.loginError = false;
  }

  async saveUserPin(selectedPin: string) {
    if (selectedPin.length === this.pinLength) {
      const hashedPin = this.cryptoService.computeSHA256Hash(
        selectedPin,
        this.userName
      );
      const foundUser = await this.userStorageService.getUserByUsername(
        this.userName
      );
      if (foundUser) {
        await this.userStorageService.saveUserPin(this.userName, hashedPin);
        this.notify.success('Pin created successfully');
        this.onLoggedIn();
      }
    }
  }

  private onLoggedIn() {
    if (this.authService.isActionCenterLogin) {
      this.navigateToActionCenter();
      return;
    }
    const isLinked = this.deviceService.isDeviceLinked();
    const isAdministrator = this.authService.isAdministrator();

    if (!isLinked) {
      this.linkDevice(isAdministrator);
      return;
    }

    if (this.deviceService.isDeviceRemoved()) {
      this.activateDevice();
      return;
    }

    this.activityService.start();

    if (this.deviceService.isWhiteboardDevice()) {
      this.navigateToWhiteBoard();
      return;
    }

    if (this.deviceService.isTheaterDevice()) {
      this.navigateToTheatre();
      return;
    }

    if (this.deviceService.isEmergencyCupboardDevice()) {
      this.navigateToEmergencyCupboard();
      return;
    }

    var canViewPatients = this.authService.isInRole([
      Roles.systemAdministrator,
      Roles.superAdministrator,
      Roles.stockIssuer,
      Roles.creditRequester,
    ]);

    if (canViewPatients) {
      this.router.navigate(['app/patients']);
      return;
    }

    var canApproveCredits = this.authService.isInRole([
      Roles.systemAdministrator,
      Roles.superAdministrator,
      Roles.creditApprover,
    ]);

    if (canApproveCredits) {
      this.router.navigate(['app/approvals']);
      return;
    }

    this.router.navigate(['app/baskets']);
    return;
  }

  private navigateToActionCenter() {
    this.router.navigate(['/app/action-center']);
  }

  private navigateToWhiteBoard() {
    this.router.navigate(['app/whiteboard']);
  }

  private navigateToTheatre() {
    this.router.navigate(['app/theatre']);
  }

  private navigateToEmergencyCupboard() {
    this.router.navigate(['app/emergency-cupboard']);
  }

  navigateBack() {
    this.showLogIn.emit();
  }

  private linkDevice(isAdministrator: boolean) {
    this.router.navigate([
      isAdministrator
        ? 'app/authentication/linkdevice'
        : 'app/authentication/deviceneedstobelinked',
    ]);
  }

  private activateDevice() {
    this.isDeviceDeactivated = true;
    this.loginError = true;
    this.alert =
      'This device has been deactivated. Contact your administrator.';
    this.router.navigate(['app/authentication/devicedeactivated']);
  }
}
